<template>
  <VTable :columns :data :default-sort="{ prop: 'first_name', order: 'asc' }" :pagination="{}" row-key="id">
    <VTableColumn
      v-slot="{ row }: { row: UserDetailed }"
      cell-class="flex-center"
      :label="$t('name')"
      max-width="200"
      prop="first_name"
      show-overflow-tooltip
      sortable
      :tooltip-content="(row: UserDetailed) => row.name"
    >
      <UserAvatar :size="26" style="margin-right: 10px" :user-id="row.id" />
      {{ row.name }}
      <VTooltip v-if="row.password_reset_tokens?.length > 0">
        <template #content>{{ $t('pending-password-reset') }}</template>
        <VBadge :size="8" style="margin-left: 5px" type="error" />
      </VTooltip>
    </VTableColumn>

    <VTableColumn :label="$t('email')" max-width="150" prop="email" show-overflow-tooltip sortable />

    <VTableColumn v-slot="{ row }" :custom-sort-transform="transformSortBy" :label="$t('function')" max-width="100" prop="function" show-overflow-tooltip sortable>
      {{ $stores.organizationFunctions.functions[row.pivot.organization_function_id]?.name }}
    </VTableColumn>

    <VTableColumn v-slot="{ row }: { row: User }" :label="$t('groups')" max-width="150" prop="groups">
      <VTag v-if="row.groups.length > 0" :closable="$can('users.update') && row.groups.length === 1 && !$inSafeGhostMode" type="primary" @close="confirmRemoveUserFromGroup(row)">
        {{ $t('name-and-count-others', { name: row.groups[0].name }, row.groups.length - 1) }}
      </VTag>
    </VTableColumn>

    <VTableColumn v-slot="{ row }: { row: User }" :label="$t('roles')" max-width="100" prop="roles" show-overflow-tooltip>
      {{
        row.roles
          .filter((role) => isAppRole(role.name) && !isAppModuleRole(role.name))
          .map((role) => $tk(role.name))
          .join(', ')
      }}
    </VTableColumn>

    <VTableColumn v-slot="{ row }: { row: User }" :label="$t('modules')" max-width="130" prop="modules" show-overflow-tooltip>
      {{
        row.roles
          .filter((role) => isAppModuleRole(role.name))
          .map((role) => $tk(role.name))
          .join(', ')
      }}
    </VTableColumn>

    <VTableColumn v-slot="{ row }: { row: User }" :label="$t('2FA')" prop="one_time_password_required" sortable>
      <VIcon v-if="row.one_time_password_required" icon="Check" type="success" />
      <VIcon v-else icon="Close" type="error" />
    </VTableColumn>

    <VTableColumn v-if="$can('users.update')" v-slot="{ row }: { row: User }" :label="$t('actions')" prop="actions">
      <slot name="actions" :row />
    </VTableColumn>
  </VTable>
</template>

<script setup lang="ts">
import { isAppModuleRole, isAppRole } from '~/stores/roles.ts'
import { type User, type UserDetailed } from '~/types.ts'

interface Props {
  data: User[]
  columns: string[]
}

defineProps<Props>()

function transformSortBy(item: User) {
  if (!item.pivot.organization_function_id) {
    return
  }

  return stores.organizationFunctions.functions[item.pivot.organization_function_id]?.name
}

async function confirmRemoveUserFromGroup(user: User) {
  await $confirm({
    title: $t('remove-user-from-group'),
    message: $t('if-you-proceed-with-this-action-the-user-email-will-be-removed-from-group-name', { email: user.email, name: user.groups[0].name }),
  })
  removeUserFromGroup(user)
}

async function removeUserFromGroup(user: User) {
  const name = user.groups[0].name
  const groupIds = user.groups.slice(1).map((group) => group.id)

  try {
    await stores.users.update({ id: user.id, group_ids: groupIds })
    $message.success($t('the-user-email-has-been-removed-from-group-name', { email: user.email, name }))
  } catch {
    $message.error($t('api-error'))
  }
}
</script>
